import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMoneyBillWave, 
    faUsers, 
    faClipboardCheck, 
    faChartBar,
    faCheck,
    faExclamationTriangle,
    faClock,
    faFileExport,
    faPlus,
    faArrowUp,
    faArrowDown,
    faMinus,
    faEye,
    faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from '../../Api';
import ProjectsStatsCards from './ProjectsStatsCards';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const RespAdminDashboard = () => {
    const [dashboardData, setDashboardData] = useState({
        payments: {
            totalBatches: 0,
            pendingBatches: 0,
            completedBatches: 0,
            unpaidPayments: 0,
            totalAmount: 0,
            recentBatches: [],
            pendingValidationBatches: 0,
            statusCounts: {
                draft: 0,
                locked: 0,
                validated: 0,
                exported: 0,
                confirmed: 0
            }
        },
        verification: {
            totalInterviewers: 0,
            verified: 0,
            unverified: 0,
            verificationRate: 0,
            pendingVerifications: []
        },
        controllerVerification: {
            totalControllers: 0,
            verified: 0,
            unverified: 0,
            verificationRate: 0,
            pendingVerifications: []
        },
        stats: {
            thisMonth: 0,
            lastMonth: 0,
            trend: 0
        }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [chartData, setChartData] = useState(null);
    const [chartData2, setChartData2] = useState(null); // Second chart for payment breakdown
    const [chartLoading, setChartLoading] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState({});

    useEffect(() => {
        fetchDashboardData();
        fetchChartData();
    }, []);

    useEffect(() => {
        fetchChartData();
    }, [selectedYear, selectedMonth]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError('');

            // Calculate date ranges for current and previous month
            const now = new Date();
            const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

            // Fetch payment batches data - current month and last month
            const [paymentResponse, lastMonthPaymentResponse, verificationResponse, controllerVerificationResponse, unpaidResponse] = await Promise.all([
                api.get('/payments', { params: { per_page: 100 } }),
                api.get('/payments', { 
                    params: { 
                        per_page: 100,
                        date_from: lastMonthStart.toISOString().split('T')[0],
                        date_to: lastMonthEnd.toISOString().split('T')[0]
                    } 
                }),
                api.get('/interviewers/verification/stats'),
                api.get('/controllers/verification/stats').catch(() => ({ data: { success: false } })),
                api.get('/payments/unpaid/stats')
            ]);

            // Process payment data
            let paymentData = {
                totalBatches: 0,
                pendingBatches: 0,
                completedBatches: 0,
                unpaidPayments: 0,
                totalAmount: 0,
                recentBatches: [],
                pendingValidationBatches: 0,
                statusCounts: {
                    draft: 0,
                    locked: 0,
                    validated: 0,
                    exported: 0,
                    confirmed: 0
                }
            };

            // Process current month data
            let currentMonthAmount = 0;
            if (paymentResponse.data.success) {
                const batches = paymentResponse.data.data.data || [];
                paymentData.totalBatches = paymentResponse.data.data.pagination?.total || 0;
                paymentData.pendingBatches = batches.filter(b => ['draft', 'locked'].includes(b.status)).length;
                paymentData.completedBatches = batches.filter(b => ['exported', 'validated', 'confirmed'].includes(b.status)).length;
                paymentData.pendingValidationBatches = batches.filter(b => b.status === 'locked').length;
                paymentData.recentBatches = batches.slice(0, 5);
                paymentData.totalAmount = batches.reduce((sum, b) => sum + (parseFloat(b.totals_net) || 0), 0);
                currentMonthAmount = paymentData.totalAmount;
                
                // Count by status
                batches.forEach(batch => {
                    if (paymentData.statusCounts.hasOwnProperty(batch.status)) {
                        paymentData.statusCounts[batch.status]++;
                    }
                });
            }

            // Process last month data for trend calculation
            let lastMonthAmount = 0;
            if (lastMonthPaymentResponse.data.success) {
                const lastMonthBatches = lastMonthPaymentResponse.data.data.data || [];
                lastMonthAmount = lastMonthBatches.reduce((sum, b) => sum + (parseFloat(b.totals_net) || 0), 0);
            }

            // Process unpaid payments
            if (unpaidResponse.data.success) {
                paymentData.unpaidPayments = unpaidResponse.data.data.total || 0;
            }

            // Process verification data
            let verificationData = {
                totalInterviewers: 0,
                verified: 0,
                unverified: 0,
                verificationRate: 0,
                pendingVerifications: []
            };

            if (verificationResponse.data.success) {
                verificationData = {
                    totalInterviewers: verificationResponse.data.data.total || 0,
                    verified: verificationResponse.data.data.verified || 0,
                    unverified: verificationResponse.data.data.unverified || 0,
                    verificationRate: verificationResponse.data.data.verified_percentage || 0
                };
            }

            // Process controller verification data
            let controllerVerificationData = {
                totalControllers: 0,
                verified: 0,
                unverified: 0,
                verificationRate: 0,
                pendingVerifications: []
            };

            if (controllerVerificationResponse.data.success) {
                controllerVerificationData = {
                    totalControllers: controllerVerificationResponse.data.data.total || 0,
                    verified: controllerVerificationResponse.data.data.verified || 0,
                    unverified: controllerVerificationResponse.data.data.unverified || 0,
                    verificationRate: controllerVerificationResponse.data.data.verified_percentage || 0
                };
            }

            // Calculate trend percentage
            let trendPercentage = 0;
            if (lastMonthAmount > 0) {
                trendPercentage = ((currentMonthAmount - lastMonthAmount) / lastMonthAmount) * 100;
            } else if (currentMonthAmount > 0) {
                trendPercentage = 100; // If last month was 0 but current month has data, show 100% increase
            }

            setDashboardData({
                payments: paymentData,
                verification: verificationData,
                controllerVerification: controllerVerificationData,
                stats: {
                    thisMonth: currentMonthAmount,
                    lastMonth: lastMonthAmount,
                    trend: Math.round(trendPercentage) || 0
                }
            });

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Erreur lors du chargement des données du tableau de bord');
        } finally {
            setLoading(false);
        }
    };

    const getTrendIcon = (trend) => {
        if (trend > 0) return faArrowUp;
        if (trend < 0) return faArrowDown;
        return faMinus;
    };

    const getTrendColor = (trend) => {
        if (trend > 0) return 'text-green-600';
        if (trend < 0) return 'text-red-600';
        return 'text-gray-600';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'draft': return 'text-yellow-600 bg-yellow-100';
            case 'locked': return 'text-purple-600 bg-purple-100';
            case 'validated': return 'text-blue-600 bg-blue-100';
            case 'exported': return 'text-green-600 bg-green-100';
            case 'confirmed': return 'text-green-600 bg-green-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'draft': return 'Brouillon';
            case 'locked': return 'Verrouillé';
            case 'validated': return 'Validé';
            case 'exported': return 'Exporté';
            case 'confirmed': return 'Confirmé';
            default: return status;
        }
    };

    const getSurveyNames = (batch) => {
        if (batch.surveys && batch.surveys.length > 0) {
            return batch.surveys.map(survey => survey.title || survey.name).join(', ');
        }
        return 'Aucune enquête';
    };

    const getPeriodDates = (batch) => {
        if (batch.period_start && batch.period_end) {
            const startDate = new Date(batch.period_start).toLocaleDateString('fr-FR');
            const endDate = new Date(batch.period_end).toLocaleDateString('fr-FR');
            return `${startDate} - ${endDate}`;
        }
        return '';
    };

    const fetchChartData = async () => {
        try {
            setChartLoading(true);
            const response = await api.get('/payments/weekly-stats', {
                params: {
                    year: selectedYear,
                    month: selectedMonth
                }
            });

            if (response.data.success) {
                const data = response.data.data;
                
                // Update available years and months from API
                if (data.available_years && data.available_months) {
                    setAvailableYears(data.available_years);
                    setAvailableMonths(data.available_months);
                    
                    // Only set defaults on the first load when we don't have available data yet
                    if (availableYears.length === 0) {
                        const defaultYear = data.available_years[0] || new Date().getFullYear();
                        // Default to current month since we show all months regardless of data availability
                        const defaultMonth = new Date().getMonth() + 1;
                        setSelectedYear(defaultYear);
                        setSelectedMonth(defaultMonth);
                    }
                }
                
                // Chart 1: Prepare chart data for total amounts
                const labels = data.weekly_data ? data.weekly_data.map(week => week.week) : [];
                const actualData = data.weekly_data ? data.weekly_data.map(week => week.total_amount) : [];
                
                const maxValue = actualData.length > 0 ? Math.max(...actualData) : 1;
                const minVisibleAmount = Math.max(1000, maxValue * 0.01);
                
                const displayedData = actualData.map(amount => {
                    if (amount === 0 || (amount > 0 && amount < minVisibleAmount)) {
                        return minVisibleAmount;
                    }
                    return amount;
                });
                
                const chartConfig = {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Montant Total (MAD)',
                            data: displayedData,
                            backgroundColor: 'rgba(220, 38, 38, 0.8)',
                            borderColor: 'rgba(220, 38, 38, 1)',
                            borderWidth: 1,
                            actualData: actualData,
                        },
                    ],
                };

                setChartData(chartConfig);
                
                // Chart 2: Payment breakdown by status
                const paidData = data.weekly_data ? data.weekly_data.map(week => week.paid_amount || 0) : [];
                const unpaidData = data.weekly_data ? data.weekly_data.map(week => week.unpaid_amount || 0) : [];
                const insufficientData = data.weekly_data ? data.weekly_data.map(week => week.insufficient_amount || 0) : [];
                const declinedData = data.weekly_data ? data.weekly_data.map(week => week.declined_amount || 0) : [];
                
                const allAmounts = [...paidData, ...unpaidData, ...insufficientData, ...declinedData];
                const maxValue2 = allAmounts.length > 0 ? Math.max(...allAmounts) : 1;
                const minVisibleAmount2 = Math.max(500, maxValue2 * 0.01);
                
                const boostForVisibility = (amount) => {
                    if (amount === 0) return 0;
                    return amount < minVisibleAmount2 ? minVisibleAmount2 : amount;
                };
                
                const paidDisplayData = paidData.map(boostForVisibility);
                const unpaidDisplayData = unpaidData.map(boostForVisibility);
                const insufficientDisplayData = insufficientData.map(boostForVisibility);
                const declinedDisplayData = declinedData.map(boostForVisibility);
                
                const chartConfig2 = {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Payé (MAD)',
                            data: paidDisplayData,
                            backgroundColor: 'rgba(34, 197, 94, 0.8)',
                            borderColor: 'rgba(34, 197, 94, 1)',
                            borderWidth: 1,
                            actualData: paidData,
                            stack: 'paid',
                            order: 1,
                        },
                        {
                            label: 'Non Payé (MAD)',
                            data: unpaidDisplayData,
                            backgroundColor: 'rgba(234, 179, 8, 0.8)',
                            borderColor: 'rgba(234, 179, 8, 1)',
                            borderWidth: 1,
                            actualData: unpaidData,
                            stack: 'unpaid',
                            order: 2,
                        },
                        {
                            label: 'Montant Insuffisant (MAD)',
                            data: insufficientDisplayData,
                            backgroundColor: 'rgba(245, 158, 11, 0.8)',
                            borderColor: 'rgba(245, 158, 11, 1)',
                            borderWidth: 1,
                            actualData: insufficientData,
                            stack: 'unpaid',
                            order: 3,
                        },
                        {
                            label: 'Refusé (MAD)',
                            data: declinedDisplayData,
                            backgroundColor: 'rgba(217, 119, 6, 0.8)',
                            borderColor: 'rgba(217, 119, 6, 1)',
                            borderWidth: 1,
                            actualData: declinedData,
                            stack: 'unpaid',
                            order: 4,
                        },
                    ],
                };

                setChartData2(chartConfig2);
            }
        } catch (error) {
            console.error('Error fetching chart data:', error);
            setChartData(null);
            setChartData2(null);
        } finally {
            setChartLoading(false);
        }
    };

    // Month labels for display
    const monthLabels = {
        1: 'Janvier',
        2: 'Février',
        3: 'Mars',
        4: 'Avril',
        5: 'Mai',
        6: 'Juin',
        7: 'Juillet',
        8: 'Août',
        9: 'Septembre',
        10: 'Octobre',
        11: 'Novembre',
        12: 'Décembre',
    };

    // Get all months (always show all 12 months regardless of data availability)
    const getAllMonths = () => {
        return Object.keys(monthLabels).map(monthValue => ({
            value: parseInt(monthValue),
            label: monthLabels[monthValue]
        }));
    };

    // Handle year change
    const handleYearChange = (newYear) => {
        setSelectedYear(newYear);
        // Keep the current month selection since we show all months
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>
                </div>
                <div className="text-center relative z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button 
                        onClick={fetchDashboardData}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 p-4 sm:p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-6 sm:mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-2 sm:mb-3">
                                    Tableau de Bord Administratif
                                </h1>
                                <p className="text-sm sm:text-base text-gray-600">Gestion des paiements et vérifications</p>
                            </div>
                        </div>
                    </div>

                    <ProjectsStatsCards />

                    {/* Main Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
                        
                        {/* Left Column - Stats and Recent Payments */}
                        <div className="lg:col-span-8">
                            {/* Overview Stats */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
                                {/* Payment Batches */}
                                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="text-xl sm:text-2xl text-red-600" />
                                        <div className="text-right">
                                            <div className="text-xl sm:text-2xl font-bold text-gray-900">
                                                {dashboardData.payments.totalBatches}
                                            </div>
                                            <div className="text-xs sm:text-sm text-gray-600">Lots Totaux</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Unpaid Payments */}
                                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl sm:text-2xl text-red-600" />
                                        <div className="text-right">
                                            <div className="text-xl sm:text-2xl font-bold text-gray-900">
                                                {dashboardData.payments.unpaidPayments}
                                            </div>
                                            <div className="text-xs sm:text-sm text-gray-600">Non Payés</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Verification Stats */}
                                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <FontAwesomeIcon icon={faClipboardCheck} className="text-xl sm:text-2xl text-red-600" />
                                        <div className="text-right">
                                            <div className="text-xl sm:text-2xl font-bold text-gray-900">
                                                {dashboardData.verification.verificationRate}%
                                            </div>
                                            <div className="text-xs sm:text-sm text-gray-600">Vérification</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Weekly Payment Chart */}
                            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 mb-6">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-3 sm:space-y-0">
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Paiements Hebdomadaires</h3>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                                        {/* Year Filter */}
                                        <select
                                            value={selectedYear}
                                            onChange={(e) => handleYearChange(parseInt(e.target.value))}
                                            className="w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            disabled={availableYears.length === 0}
                                        >
                                            {availableYears.length > 0 ? (
                                                availableYears.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))
                                            ) : (
                                                <option value={selectedYear}>Chargement...</option>
                                            )}
                                        </select>
                                        
                                        {/* Month Filter */}
                                        <select
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                            className="w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                        >
                                            {getAllMonths().map(month => (
                                                <option key={month.value} value={month.value}>{month.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                
                                {chartLoading ? (
                                    <div className="flex items-center justify-center h-48 sm:h-64">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                                        <span className="ml-2 text-gray-600 text-sm sm:text-base">Chargement du graphique...</span>
                                    </div>
                                ) : chartData ? (
                                    <div className="h-48 sm:h-64">
                                        <Bar
                                            data={chartData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: false
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: function(context) {
                                                                const actualValue = context.dataset.actualData ? 
                                                                    context.dataset.actualData[context.dataIndex] : 
                                                                    context.parsed.y;
                                                                return formatCurrency(actualValue);
                                                            }
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    x: {
                                                        ticks: {
                                                            maxRotation: window.innerWidth < 768 ? 45 : 0,
                                                            minRotation: window.innerWidth < 768 ? 45 : 0,
                                                            font: {
                                                                size: 11
                                                            }
                                                        }
                                                    },
                                                    y: {
                                                        beginAtZero: true,
                                                        min: 0,
                                                        ticks: {
                                                            callback: function(value) {
                                                                return formatCurrency(value);
                                                            }
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-48 sm:h-64 text-gray-500">
                                        <div className="text-center">
                                            <FontAwesomeIcon icon={faChartBar} className="text-3xl sm:text-4xl mb-2" />
                                            <p className="text-sm sm:text-base">Aucune donnée disponible</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Payment Status Breakdown Chart */}
                            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 mb-6">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-3 sm:space-y-0">
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Répartition des Paiements par Statut</h3>
                                </div>
                                
                                {chartLoading ? (
                                    <div className="flex items-center justify-center h-48 sm:h-64">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                                        <span className="ml-2 text-gray-600 text-sm sm:text-base">Chargement du graphique...</span>
                                    </div>
                                ) : chartData2 ? (
                                    <div className="h-64 sm:h-80">
                                        <Bar
                                            data={chartData2}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: true,
                                                        position: 'top',
                                                        labels: {
                                                            boxWidth: 15,
                                                            padding: 10,
                                                            font: {
                                                                size: 12
                                                            }
                                                        }
                                                    },
                                                    tooltip: {
                                                        mode: 'index',
                                                        intersect: false,
                                                        callbacks: {
                                                            label: function(context) {
                                                                const actualValue = context.dataset.actualData ? 
                                                                    context.dataset.actualData[context.dataIndex] : 
                                                                    context.parsed.y;
                                                                return context.dataset.label + ': ' + formatCurrency(actualValue);
                                                            },
                                                            footer: function(tooltipItems) {
                                                                let total = 0;
                                                                tooltipItems.forEach(function(item) {
                                                                    const actualValue = item.dataset.actualData ? 
                                                                        item.dataset.actualData[item.dataIndex] : 
                                                                        item.parsed.y;
                                                                    total += actualValue;
                                                                });
                                                                return 'Total: ' + formatCurrency(total);
                                                            }
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    x: {
                                                        stacked: true,
                                                        ticks: {
                                                            maxRotation: window.innerWidth < 768 ? 45 : 0,
                                                            minRotation: window.innerWidth < 768 ? 45 : 0,
                                                            font: {
                                                                size: 11
                                                            }
                                                        }
                                                    },
                                                    y: {
                                                        stacked: true,
                                                        beginAtZero: true,
                                                        min: 0,
                                                        ticks: {
                                                            callback: function(value) {
                                                                return formatCurrency(value);
                                                            }
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-48 sm:h-64 text-gray-500">
                                        <div className="text-center">
                                            <FontAwesomeIcon icon={faChartBar} className="text-3xl sm:text-4xl mb-2" />
                                            <p className="text-sm sm:text-base">Aucune donnée disponible</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Recent Payment Batches */}
                            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">Lots de Paiement Récents</h3>
                                    <Link 
                                        to="/payments"
                                        className="text-red-600 hover:text-red-700 font-medium text-sm whitespace-nowrap"
                                    >
                                        Voir tout →
                                    </Link>
                                </div>
                                
                                {dashboardData.payments.recentBatches.length > 0 ? (
                                    <div className="space-y-3 sm:space-y-4">
                                        {dashboardData.payments.recentBatches.slice(0, 3).map((batch) => (
                                            <Link 
                                                key={batch.id} 
                                                to={`/payments/${batch.id}`}
                                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors cursor-pointer"
                                            >
                                                <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0">
                                                        <FontAwesomeIcon icon={faMoneyBillWave} className="text-red-600" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="font-medium text-gray-900 text-sm sm:text-base break-words">
                                                            {batch.name || `Lot #${batch.id}`}
                                                        </div>
                                                        <div className="text-xs sm:text-sm text-gray-600 break-words mt-1">
                                                            {getSurveyNames(batch)} - {getPeriodDates(batch)}
                                                        </div>
                                                        <div className="text-xs text-gray-400 mt-1">
                                                            Créé le {new Date(batch.created_at).toLocaleDateString('fr-FR')}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex sm:flex-col sm:text-right items-center sm:items-end gap-2 sm:gap-1 flex-shrink-0">
                                                    <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(batch.status)}`}>
                                                        {getStatusText(batch.status)}
                                                    </div>
                                                    <div className="text-xs sm:text-sm text-gray-600 font-medium whitespace-nowrap">
                                                        {formatCurrency(parseFloat(batch.totals_net) || 0)}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <FontAwesomeIcon icon={faClock} className="text-3xl sm:text-4xl text-red-600 mb-3" />
                                        <p className="text-sm sm:text-base">Aucun lot de paiement récent</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Column - Verification */}
                        <div className="lg:col-span-4 space-y-4 sm:space-y-6">
                            {/* Payment Status */}
                            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Statut Paiements</h3>
                                
                                <div className="space-y-4">
                                    <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                                        <div className="text-2xl font-bold text-red-600">
                                            {dashboardData.payments.pendingValidationBatches}
                                        </div>
                                        <div className="text-sm text-gray-600">Paiements en Attente de Validation</div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        {Object.entries(dashboardData.payments.statusCounts).map(([status, count]) => (
                                            <div key={status} className="flex justify-between text-sm">
                                                <span className="text-gray-600">{getStatusText(status)}</span>
                                                <span className="font-bold text-gray-900">{count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Verification Overview */}
                            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Vérification Enquêteurs</h3>
                            
                            <div className="space-y-4">
                                <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                                    <div className="text-2xl font-bold text-red-600">
                                        {dashboardData.verification.verified}/{dashboardData.verification.totalInterviewers}
                                    </div>
                                    <div className="text-sm text-gray-600">Enquêteurs Vérifiés</div>
                                </div>
                                
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Total</span>
                                        <span className="font-bold text-gray-900">{dashboardData.verification.totalInterviewers}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Vérifiés</span>
                                        <span className="font-bold text-green-600">{dashboardData.verification.verified}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Non vérifiés</span>
                                        <span className="font-bold text-red-600">{dashboardData.verification.unverified}</span>
                                    </div>
                                </div>
                                
                                <Link 
                                    to="/interviewers/verification"
                                    className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-medium transition-colors text-center block"
                                >
                                    Gérer la Vérification
                                </Link>
                            </div>
                            </div>

                            {/* Controller Verification Overview */}
                            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Vérification Contrôleurs</h3>
                            
                            <div className="space-y-4">
                                <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                                    <div className="text-2xl font-bold text-red-600">
                                        {dashboardData.controllerVerification.verified}/{dashboardData.controllerVerification.totalControllers}
                                    </div>
                                    <div className="text-sm text-gray-600">Contrôleurs Vérifiés</div>
                                </div>
                                
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Total</span>
                                        <span className="font-bold text-gray-900">{dashboardData.controllerVerification.totalControllers}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Vérifiés</span>
                                        <span className="font-bold text-green-600">{dashboardData.controllerVerification.verified}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Non vérifiés</span>
                                        <span className="font-bold text-red-600">{dashboardData.controllerVerification.unverified}</span>
                                    </div>
                                </div>
                                
                                <Link 
                                    to="/interviewers/verification"
                                    className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-medium transition-colors text-center block"
                                >
                                    Gérer la Vérification
                                </Link>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RespAdminDashboard;
